// src/pages/Employee/CreateEmployee/FileUploadField.tsx
import React, { useRef, useState } from 'react';
import { Label, FormFeedback, Button } from 'reactstrap';
import { Eye, Trash2, UploadCloud, FileText, Image as ImageIcon, Paperclip } from 'react-feather';
import { toast } from 'react-toastify';
import FilePreviewModal from './filepreviewmodal';
import { FileValue, emptyFile } from '../../types/employee.types';

interface FileUploadFieldProps {
  label: string;
  value: FileValue;
  onChange: (value: FileValue) => void;
  error?: string;
  required?: boolean;
  accept?: string;
  maxSizeMb?: number;
  helpText?: string;
}

const DEFAULT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp';

const iconFor = (value: FileValue) => {
  const name = (value.file?.name || value.name || '').toLowerCase();
  const type = value.file?.type || '';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return <FileText size={16} />;
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/.test(name)) return <ImageIcon size={16} />;
  return <Paperclip size={16} />;
};

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  label,
  value,
  onChange,
  error,
  required,
  accept = DEFAULT_ACCEPT,
  maxSizeMb = 5,
  helpText,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const hasFile = Boolean(value.file || value.existingUrl);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`${label}: file must be under ${maxSizeMb}MB`);
      e.target.value = '';
      return;
    }

    onChange({ file, existingUrl: value.existingUrl, name: file.name });
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange(emptyFile());
  };

  return (
    <div className="mb-3">
      <Label className="fw-bold text-muted small text-uppercase">
        {label} {required && <span className="text-danger">*</span>}
      </Label>

      {!hasFile && (
        <div
          role="button"
          onClick={() => inputRef.current?.click()}
          className={`border rounded-3 d-flex align-items-center gap-2 px-3 py-2 ${
            error ? 'border-danger' : 'border-dashed'
          }`}
          style={{ borderStyle: 'dashed', cursor: 'pointer', background: '#f8f9fa' }}
        >
          <UploadCloud size={18} className="text-primary flex-shrink-0" />
          <span className="text-muted small">Click to upload (PDF, JPG, PNG — max {maxSizeMb}MB)</span>
        </div>
      )}

      {hasFile && (
        <div className={`border rounded-3 d-flex align-items-center gap-2 px-3 py-2 ${error ? 'border-danger' : ''}`}>
          {iconFor(value)}
          <span className="small text-truncate flex-grow-1" title={value.file?.name || value.name || ''}>
            {value.file?.name || value.name}
          </span>
          <Button
            type="button"
            color="link"
            size="sm"
            className="p-0 text-primary"
            onClick={() => setPreviewOpen(true)}
            title="Preview"
          >
            <Eye size={16} />
          </Button>
          <Button
            type="button"
            color="link"
            size="sm"
            className="p-0 text-danger"
            onClick={handleRemove}
            title="Remove"
          >
            <Trash2 size={16} />
          </Button>
          <Button
            type="button"
            color="link"
            size="sm"
            className="p-0 text-secondary"
            onClick={() => inputRef.current?.click()}
            title="Replace"
          >
            <UploadCloud size={16} />
          </Button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleSelect}
        className="d-none"
      />

      {helpText && !error && <div className="text-muted small mt-1">{helpText}</div>}
      {error && <div className="text-danger small mt-1">{error}</div>}

      <FilePreviewModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        title={label}
        fileValue={value}
      />
    </div>
  );
};

export default FileUploadField;